// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Organization, Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { JSX, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { PracticeSwitchModal } from '../components/PracticeSwitchModal';
import { getUserPreferences } from '../utils/userPreferences';
import { applyLanguageFromPreferences } from '../i18n';

const STORAGE_KEY = 'carepoint-current-organization';

interface OrganizationContextValue {
  /** List of organizations the user has access to */
  organizations: Organization[];
  /** Currently selected organization/practice */
  currentOrganization: Organization | null;
  /** Request to change the current organization (shows confirmation modal) */
  requestOrganizationSwitch: (org: Organization) => void;
  /** Whether organizations are still loading */
  loading: boolean;
  /** Get the organization reference string for FHIR queries */
  getOrganizationFilter: () => string | undefined;
  /** Get search params with organization filter applied */
  applyOrganizationFilter: (params: Record<string, string>) => Record<string, string>;
}

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

interface OrganizationProviderProps {
  children: ReactNode;
}

export function OrganizationProvider({ children }: OrganizationProviderProps): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganizationState] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  // Modal state for confirming practice switch
  const [pendingOrganization, setPendingOrganization] = useState<Organization | null>(null);
  const [showSwitchModal, setShowSwitchModal] = useState(false);

  // Fetch organizations the user has access to
  useEffect(() => {
    const fetchOrganizations = async (): Promise<void> => {
      try {
        // Get the current user's Practitioner profile
        const profile = medplum.getProfile() as Practitioner | undefined;
        if (!profile?.id) {
          setLoading(false);
          return;
        }

        // Check if user is a project admin
        const membership = medplum.getProjectMembership();
        const isProjectAdmin = membership?.admin === true;

        let orgs: Organization[] = [];

        if (isProjectAdmin) {
          // Project admins can see ALL organizations in the project
          // They don't need PractitionerRole associations
          console.log('[OrganizationContext] User is project admin, fetching all organizations');
          orgs = await medplum.searchResources('Organization', {
            _count: '100',
            _sort: 'name',
          });
        } else {
          // Regular users: Find organizations where the user has an active PractitionerRole
          // This ensures proper project-level isolation - users only see organizations
          // they are actually associated with in the current project
          const practitionerRoles = await medplum.searchResources('PractitionerRole', {
            practitioner: `Practitioner/${profile.id}`,
            active: 'true',
            _count: '100',
          });

          // Extract unique organization IDs from PractitionerRoles
          const orgIds = new Set<string>();
          for (const role of practitionerRoles) {
            if (role.organization?.reference) {
              const orgId = role.organization.reference.replace('Organization/', '');
              orgIds.add(orgId);
            }
          }

          // If no organizations found via PractitionerRole, the user has no access
          if (orgIds.size === 0) {
            console.warn('No organizations found for user via PractitionerRole');
            setOrganizations([]);
            setLoading(false);
            return;
          }

          // Fetch the actual Organization resources
          orgs = await medplum.searchResources('Organization', {
            _id: Array.from(orgIds).join(','),
            _count: '100',
            _sort: 'name',
          });
        }

        setOrganizations(orgs);

        // Fetch the latest Practitioner profile from Medplum (not cached)
        // This ensures we get the most up-to-date user preferences including language
        let freshProfile: Practitioner | undefined = profile;
        try {
          freshProfile = await medplum.readResource('Practitioner', profile.id);
        } catch {
          // Fall back to cached profile if fetch fails
        }
        const userPrefs = getUserPreferences(freshProfile);
        const defaultOrgId = userPrefs?.defaultOrganizationId;

        // Apply user's language preference from Medplum
        if (userPrefs?.language) {
          applyLanguageFromPreferences(userPrefs.language);
        }

        // Priority for selecting organization:
        // 1. Previously selected organization from localStorage (manual session switch)
        // 2. User's default organization preference (only used on initial login)
        // 3. First available organization

        let selectedOrg: Organization | undefined;

        // First, try localStorage (user's manual session switch takes precedence)
        const savedOrgId = localStorage.getItem(STORAGE_KEY);
        if (savedOrgId) {
          selectedOrg = orgs.find((o) => o.id === savedOrgId);
        }

        // If no localStorage value, try user's default organization preference
        if (!selectedOrg && defaultOrgId) {
          selectedOrg = orgs.find((o) => o.id === defaultOrgId);
        }

        // If still no org, use first available
        if (!selectedOrg && orgs.length > 0) {
          selectedOrg = orgs[0];
        }

        if (selectedOrg) {
          setCurrentOrganizationState(selectedOrg);
          // Also save to localStorage for consistency
          if (selectedOrg.id) {
            localStorage.setItem(STORAGE_KEY, selectedOrg.id);
          }
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
      } finally {
        setLoading(false);
      }
    };

    if (medplum.getProfile()) {
      fetchOrganizations();
    } else {
      setLoading(false);
    }
  }, [medplum]);

  // Request to switch organization - shows confirmation modal
  const requestOrganizationSwitch = useCallback(
    (org: Organization): void => {
      // Don't show modal if switching to the same org
      if (org.id === currentOrganization?.id) {
        return;
      }
      setPendingOrganization(org);
      setShowSwitchModal(true);
    },
    [currentOrganization]
  );

  // Confirm the switch
  const confirmSwitch = useCallback((): void => {
    if (pendingOrganization) {
      // Close modal first
      setShowSwitchModal(false);

      // Navigate to dashboard FIRST to avoid "Patient not found" errors
      // when the current page has a patient that doesn't belong to the new org
      navigate('/dashboard');

      // Delay organization switch to allow navigation to complete
      // This prevents usePatient from running validation on the old page
      setTimeout(() => {
        setCurrentOrganizationState(pendingOrganization);
        if (pendingOrganization.id) {
          localStorage.setItem(STORAGE_KEY, pendingOrganization.id);
        }
        setPendingOrganization(null);
      }, 100);
    } else {
      setShowSwitchModal(false);
      setPendingOrganization(null);
    }
  }, [pendingOrganization, navigate]);

  // Cancel the switch
  const cancelSwitch = useCallback((): void => {
    setShowSwitchModal(false);
    setPendingOrganization(null);
  }, []);

  // Get the organization reference for FHIR queries
  const getOrganizationFilter = useCallback((): string | undefined => {
    if (!currentOrganization?.id) {
      return undefined;
    }
    return `Organization/${currentOrganization.id}`;
  }, [currentOrganization]);

  // Apply organization filter to search params
  // Uses 'organization' parameter which maps to Patient.managingOrganization
  const applyOrganizationFilter = useCallback(
    (params: Record<string, string>): Record<string, string> => {
      if (!currentOrganization?.id) {
        return params;
      }
      return {
        ...params,
        organization: `Organization/${currentOrganization.id}`,
      };
    },
    [currentOrganization]
  );

  const value = useMemo(
    (): OrganizationContextValue => ({
      organizations,
      currentOrganization,
      requestOrganizationSwitch,
      loading,
      getOrganizationFilter,
      applyOrganizationFilter,
    }),
    [
      organizations,
      currentOrganization,
      requestOrganizationSwitch,
      loading,
      getOrganizationFilter,
      applyOrganizationFilter,
    ]
  );

  return (
    <OrganizationContext.Provider value={value}>
      {children}
      {pendingOrganization && (
        <PracticeSwitchModal
          opened={showSwitchModal}
          currentOrganization={currentOrganization}
          targetOrganization={pendingOrganization}
          onConfirm={confirmSwitch}
          onCancel={cancelSwitch}
        />
      )}
    </OrganizationContext.Provider>
  );
}

export function useOrganization(): OrganizationContextValue {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
